<template>
  <v-container
    class="shift-details px-8 py-0"
  >
    <template v-if="selectedShift && showHistory">
      <v-list-item class="headline page-title pa-0">
        <v-list-item-icon class="icon">
          <v-btn
            icon
            @click="closeHistory"
          >
            <v-icon>fal fa-arrow-left</v-icon>
          </v-btn>
        </v-list-item-icon>
        <v-list-item-content class="panel-title">
          {{ $t('labels.history') }}
        </v-list-item-content>
      </v-list-item>
      <ShiftHistory
        :shift="selectedShift"
        :style="{ height: `${height - 100}px` }"
      />
    </template>
    <template v-else>
      <v-list-item
        v-if="showHeader"
        class="headline page-title pa-0"
      >
        <v-list-item-icon class="icon">
          <v-icon>fal fa-calendar</v-icon>
        </v-list-item-icon>
        <v-list-item-content class="panel-title">
          {{ $t('labels.scheduleDetails') }}
        </v-list-item-content>
        <v-list-item-action>
          <v-btn
            icon
            @click="close"
          >
            <v-icon>fal fa-times</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
      <v-row
        v-if="showDailyScheduleRedirect && $store.getters['account/canAccessDailySchedules']()"
      >
        <v-col
          class="pt-0 pb-6"
          cols="12"
        >
          <v-alert
            class="caption dense font-weight-medium mx-0 mb-0"
            color="info"
            dense
            outlined
            text
          >
            <v-icon
              slot="prepend"
              class="ml-n1 mr-3"
              color="info"
              size="12"
            >
              fas fa-info-circle
            </v-icon>
            {{ $t('descriptions.masterToDaily') }}
          </v-alert>
          <v-btn
            class="white--text mt-6 redirect"
            color="secondary"
            width="100%"
            @click="goToDailySchedule"
          >
            {{ $t('labels.masterToDaily') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-card
        class="mb-5"
        outlined
        width="100%"
      >
        <v-container
          v-if="showHeader"
          class="py-0"
        >
          <v-row>
            <v-col
              class="pb-0"
              cols="12"
            >
              <span class="grey--text text--darken-3 body-2 font-weight-medium mr-3">
                <UserName
                  :user="latestUser"
                />
              </span>
              <v-icon
                :color="userState.color"
                size="8"
              >
                fas fa-circle
              </v-icon>
              <span class="caption grey--text text--darken-2">
                {{ userState.text }}
              </span>
            </v-col>
          </v-row>
          <v-row
            class="px-0 mb-2"
            no-gutters
          >
            <v-col class="caption grey--text text--darken-1">
              <template v-for="(detail, idx) in userDetails">
                <div
                  :key="`${detail}`"
                  class="d-inline-block"
                >
                  {{ detail }}
                </div>
                <v-divider
                  v-if="idx < userDetails.length - 1"
                  :key="`${detail}-div`"
                  class="separator mx-2 d-inline pb-1"
                  vertical
                />
              </template>
              <div
                v-if="latestUser.charge"
                class="pt-1"
              >
                <v-icon
                  class="pr-1"
                  x-small
                >
                  fal fa-check
                </v-icon>
                {{ $t('labels.canActAsCharge') }}
              </div>
            </v-col>
          </v-row>
        </v-container>
        <v-tabs
          v-if="shifts.length > 1"
          id="shifts"
          v-model="tab"
          centered
          class="dense"
          color="accent"
          grow
        >
          <template v-for="(shift, idx) in shifts">
            <v-tab
              :key="`tab-${idx}`"
              :href="`#${idx}`"
              :disabled="hasChanges !== false && hasChanges !== shift.id"
            >
              <div class="shift-time">
                <span
                  :class="[shift.overtime ? 'error--text' : '']"
                >
                  {{ getShiftStartTime(shift) }}
                </span>
                <span class="px-1">
                  -
                </span>
                <span
                  :class="[shift.overtime ? 'error--text' : '']"
                >
                  {{ getShiftEndTime(shift) }}
                </span>
                <v-icon
                  v-if="hasDifferentPayrollDate(shift)"
                  class="mx-1"
                  x-small
                >
                  fas fa-level-up
                </v-icon>
                <v-icon
                  v-if="hasChanges === shift.id"
                  color="info"
                  x-small
                >
                  fal fa-exclamation-triangle
                </v-icon>
              </div>
              <div class="text-capitalize caption">
                {{ getShiftWorkingStatus(shift) }}
              </div>
            </v-tab>
            <v-tab-item
              :key="`tab-item--${idx}`"
              :value="`${idx}`"
            >
              <ShiftActivity
                :allow-cancel-nurse="allowCancelNurse"
                :allow-delete="allowDelete || !shift.id"
                :date="date"
                :shift="shift"
                :read-only="readOnly"
                :user="user"
                @canceled-create="canceledCreate"
                @created="shiftCreated"
                @removed="removeShift"
                @updated="shiftUpdated"
                @has-changes="(hasChanges) => trackChanges(hasChanges, shift.id)"
                @split-shift="splitShift(shift)"
                @show-history="openHistory"
              />
            </v-tab-item>
          </template>
          <v-btn
            v-if="!readOnly && allowAddingActivities"
            class="add-shift"
            color="grey lighten-2"
            :disabled="hasUnsavedShift"
            outlined
            :title="$tc('labels.addShift', 1)"
            @click.stop="$emit('add-shift', {date, user})"
          >
            <v-icon
              class="grey--text text--darken-2"
            >
              fal fa-plus
            </v-icon>
          </v-btn>
        </v-tabs>
        <template v-else>
          <v-divider class="mt-0" />
          <v-container class="pa-0 d-inline-flex">
            <v-container class="py-0 grey--text text--darken-3 body-2 py-2 d-inline-block">
              <div class="shift-time">
                <span
                  :class="[shifts[0].overtime ? 'error--text' : '']"
                >
                  {{ getShiftStartTime(shifts[0]) }}
                </span>
                <span class="px-1">
                  -
                </span>
                <span
                  :class="[shifts[0].overtime ? 'error--text' : '']"
                >
                  {{ getShiftEndTime(shifts[0]) }}
                </span>
                <v-icon
                  v-if="hasDifferentPayrollDate(shifts[0])"
                  class="mx-1 text--darken-3"
                  color="grey"
                  x-small
                >
                  fas fa-level-up
                </v-icon>
                <v-icon
                  v-if="hasChanges"
                  color="info"
                  x-small
                >
                  fal fa-exclamation-triangle
                </v-icon>
              </div>
              <span class="text-capitalize grey--text text--darken-3 caption">
                {{ getShiftWorkingStatus(shifts[0]) }}
              </span>
            </v-container>
            <v-container
              v-if="!readOnly && allowAddingActivities"
              class="pa-0"
            >
              <v-btn
                class="add-shift"
                color="grey lighten-2"
                :disabled="hasUnsavedShift"
                outlined
                :title="$tc('labels.addShift', 1)"
                @click="$emit('add-shift', {date, user})"
              >
                <v-icon
                  class="grey--text text--darken-2"
                >
                  fal fa-plus
                </v-icon>
              </v-btn>
            </v-container>
          </v-container>
          <v-divider class="mt-0" />
          <ShiftActivity
            :allow-cancel-nurse="allowCancelNurse"
            :allow-delete="allowDelete || !shifts[0].id"
            :date="date"
            :shift="shifts[0]"
            :read-only="readOnly"
            :user="user"
            @canceled-create="canceledCreate"
            @created="shiftCreated"
            @removed="removeShift"
            @updated="shiftUpdated"
            @has-changes="(hasChanges) => trackChanges(hasChanges, shifts[0].id)"
            @split-shift="splitShift(shifts[0])"
            @show-history="openHistory"
          />
        </template>
      </v-card>
      <SplitShiftDialog
        v-if="selectedShift"
        :date="date"
        :shift="selectedShift"
        :show="showSplitShiftDialog"
        :user="user"
        @close="hideSplitShiftDialog"
        @saved="splitSaved"
      />
    </template>
  </v-container>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import Vue from 'vue';
import ShiftActivity from '@/components/scheduling/ShiftActivity';
import ShiftHistory from '@/components/scheduling/ShiftHistory';
import SplitShiftDialog from '@/components/scheduling/SplitShiftDialog';
import { isWorkingShiftForDisplay } from '@/utils/scheduling';
import { DATE_FORMAT, getUnsavedChangesDialogProps } from '@/utils/ui';
import UserName from '@/components/scheduling/UserName';

export default {
  components: {
    ShiftActivity,
    ShiftHistory,
    SplitShiftDialog,
    UserName
  },
  props: {
    allowAddingActivities: {
      default: true,
      type: Boolean
    },
    allowCancelNurse: {
      default: true,
      type: Boolean
    },
    allowDelete: {
      default: false,
      type: Boolean
    },
    date: {
      default: function () {
        return moment();
      },
      type: Object,
      required: true
    },
    maxDate: {
      default: '',
      type: String
    },
    minDate: {
      default: '',
      type: String
    },
    readOnly: {
      default: false,
      type: Boolean
    },
    selectedShiftIndex: {
      default: 0,
      type: Number
    },
    shifts: {
      default: function () {
        return [];
      },
      type: Array,
      required: true
    },
    showDailyScheduleRedirect: {
      default: false,
      type: Boolean
    },
    showHeader: {
      default: true,
      type: Boolean
    },
    user: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      hasChanges: false,
      refresh: false,
      selectedShift: null,
      showHistory: false,
      showSplitShiftDialog: false,
      tab: String(this.selectedShiftIndex),
      height: 500
    };
  },
  computed: {
    activeDepartment () {
      return this.$store.getters['org/getActiveDepartment']();
    },
    dateFormatString () {
      return this.$store.getters['org/getDateFormatLongWithDoW']();
    },
    hasUnsavedShift () {
      return _.filter(this.shifts, (s) => !s.id).length > 0;
    },
    headers () {
      return [
        { sortable: false, text: '', value: 'shift' }
      ];
    },
    latestUser () {
      return this.$store.state.org.employees[this.user.userId];
    },
    shiftFlags () {
      return this.$store.state.org.flags.reduce((flags, value) => {
        flags[value.id] = value;
        return flags;
      }, {});
    },
    shiftName () {
      const shiftType = this.getShiftTypeById(this.shifts[0].typeId);
      return shiftType.name;
    },
    userDetails () {
      return [
        this.latestUser.jobTypeName,
        this.latestUser.jobStatusName,
        this.latestUser.shiftTypeName
      ];
    },
    userState () {
      const colors = {
        active: 'success',
        inactive: 'grey'
      };
      let color = colors[this.latestUser.state];
      let text = this.$t(`labels.${this.latestUser.state}`);
      return {
        color,
        text
      };
    }
  },
  mounted: function () {
    this.updateHeight();
    window.addEventListener('resize', _.debounce(this.updateHeight, 200));
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.updateHeight);
  },
  methods: {
    canceledCreate () {
      this.hasChanges = false;
      this.$store.commit('unmark_unsaved_changes', this);
      this.$emit('has-changes', false);
      this.$emit('canceled-create', this.tab);
    },
    close () {
      // hasChanges has the shift ID value and the value can be null for new shifts so have to
      // explicitily check for boolean
      if (this.hasChanges !== false) {
        this.$dialog.confirm(getUnsavedChangesDialogProps(this)).then(() => {}).catch(() => {
          this.$store.commit('unmark_unsaved_changes', this);
          this.$emit('has-changes', false);
          this.$emit('close');
        });
      } else {
        this.$emit('close');
      }
    },
    closeHistory () {
      this.selectedShift = null;
      this.showHistory = false;
    },
    customGroup (items, groupBy) {
      const sortedItems = this.sortShifts(items);
      const key = groupBy[0];
      const groups = [];
      for (let i = 0, count = sortedItems.length; i < count; i++) {
        groups.push(
          {
            name: sortedItems[i][key],
            items: [sortedItems[i]]
          }
        );
      }
      return groups;
    },
    dispatch (action, payload) {
      // This function is added mainly for easy of mocking during in unit tests.
      return new Promise((resolve, reject) => {
        this.$store.dispatch(action, payload).then(response => {
          resolve(response);
        }).catch(error => {
          reject(error);
        });
      });
    },
    getShiftTypeById (id) {
      return this.$store.getters['org/getShiftTypeById'](id);
    },
    getShiftEndTime (shift) {
      let time = '';
      if (shift) {
        const shiftType = this.getShiftTypeById(shift.typeId);
        const end = shift.endTime ? shift.endTime : shiftType.endTime;
        time = _.split(end, ':', 2).join(':');
      }

      return time;
    },
    getShiftStartTime (shift) {
      let time = '';
      if (shift) {
        const shiftType = this.getShiftTypeById(shift.typeId);
        const start = shift.startTime ? shift.startTime : shiftType.startTime;
        time = _.split(start, ':', 2).join(':');
      }

      return time;
    },
    getShiftWorkingStatus (shift) {
      let status = this.$t('labels.onDuty');
      if (shift && this.isShiftNonDuty(shift)) {
        if (shift.canceled) {
          status = this.$t('labels.canceled');
        } else if (shift.giveaway) {
          status = this.$t('labels.giveaway');
        } else if (shift.swapped) {
          status = this.$t('labels.swapped');
        } else {
          status = this.$t('labels.nonDuty');
        }
      } else if (shift && shift.onCall) {
        status = this.$t('labels.onCall');
      }

      return status;
    },
    goToDailySchedule () {
      this.$store.commit('scheduling/set_daily_schedule_filters', {
        date: moment(this.date).format(DATE_FORMAT),
        user: this.user
      });
      this.$store.commit('close_panels');
      this.$router.push({
        name: 'manageDailySchedule'
      });
    },
    hasDifferentPayrollDate (shift) {
      let hasDifferentPayrollDate = false;
      if (shift.payrollDate) {
        hasDifferentPayrollDate = !moment(shift.payrollDate).isSame(moment(shift.date));
      }

      return hasDifferentPayrollDate;
    },
    hideSplitShiftDialog () {
      this.showSplitShiftDialog = false;
      this.selectedShift = null;
    },
    isShiftNonDuty (shift) {
      return !isWorkingShiftForDisplay(shift, this.shiftFlags);
    },
    moment,
    openHistory (shift) {
      this.selectedShift = shift;
      this.showHistory = true;
    },
    removeShift (shift) {
      this.hasChanges = false;
      this.$store.commit('unmark_unsaved_changes', this);
      this.$emit('has-changes', false);
      this.$emit('removed', shift);
    },
    sortShifts (shifts) {
      return _.sortBy(shifts, [
        (shift) => {
          const shiftType = this.getShiftTypeById(shift.typeId);
          const shiftStartTime = shift.startTime ? shift.startTime : shiftType.startTime;
          let day = moment(shiftStartTime, 'HH:mm:ss');
          if (shiftStartTime < shiftType.startTime) {
            day.add(1, 'day').valueOf();
          }

          return day.valueOf();
        }
      ]);
    },
    splitSaved (data) {
      this.hasChanges = false;
      this.$store.commit('unmark_unsaved_changes', this);
      this.$emit('has-changes', false);
      this.$emit('saved-splits', data);
    },
    splitShift (shift) {
      this.selectedShift = shift;
      this.showSplitShiftDialog = true;
    },
    trackChanges (hasChanges, id) {
      if (hasChanges) {
        this.hasChanges = id;
        this.$store.commit('mark_unsaved_changes', this);
      } else {
        this.hasChanges = false;
        this.$store.commit('unmark_unsaved_changes', this);
      }
      this.$emit('has-changes', hasChanges);
    },
    updateHeight () {
      if (this.$el.parentElement) {
        this.height = this.$el.parentElement.clientHeight;
      }
    },
    shiftCreated (shift) {
      this.hasChanges = false;
      this.$store.commit('unmark_unsaved_changes', this);
      this.$emit('has-changes', false);
      this.$emit('created', shift);
      this.shifts.push(shift);
      this.refresh = !this.refresh;
    },
    shiftUpdated (data) {
      const { shift } = data;
      this.hasChanges = false;
      this.$store.commit('unmark_unsaved_changes', this);
      this.$emit('has-changes', false);
      this.$emit('updated', data);
      const index = _.findIndex(this.shifts, (s) => s.id === shift.id);
      if (index >= 0) {
        Vue.set(this.shifts, index, shift);
        this.refresh = !this.refresh;
      }
    }
  }
};
</script>

<style lang="scss">
.shift-details {
  @include shift-activity();
  .add-shift {
    border-color: map-get($grey, 'lighten-2');
    border-width: 0px 0px 0px 1px;
    border-radius: 0px;
    float: right;
    height: 57px !important;
    min-width: 50px !important;
    width: 50px;
  }

  .user {
    flex: none;
    width: 156px;
  }

  .v-tabs {
    .v-tabs-bar {
      border-bottom: 1px solid map-get($grey, 'lighten-2') !important;
      border-top: 1px solid map-get($grey, 'lighten-2') !important;
      height: 56px;
      margin-right: 0px !important;
      .v-tab {
        display: inline-block;
        margin-left: 0px;
        padding-left: 12px;
        padding-top: 10px;
        text-align: left;
        &.v-tab--active {
          color: map-get($grey, 'darken-3') !important;
        }
      }
    }
  }
  .shift-time > span {
    vertical-align: middle;
  }
}
</style>
